import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance, { API_BASE_URL } from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import TrDateInput from '../components/TrDateInput';
import ProjeGorunumleri from '../components/ProjeGorunumleri';
import { Building2, Search, LayoutGrid, List, Grid3x3, Camera, MapPin } from 'lucide-react';

interface Proje {
    projeID: number;
    projeAdi: string;
    ada: string | null;
    parsel: string | null;
    eklenmeTarihi: string;
    baslamaTarihi: string | null;
    bitisTarihi: string | null;
    projeLogo: string | null;
    takipNo: string;
    adres: string | null;
    sehir: string | null;
    kdv: number;
    projeDurumu: string;
    aktifMi: boolean;
}

interface DaireOzet {
    daireID: number;
    projeID: number;
    daireDurumu: string;
    listeFiyati?: number;
    daireTipi?: { tip: string } | null;
}

interface ProjeForm {
    projeAdi: string;
    ada: string;
    parsel: string;
    baslamaTarihi: string;
    bitisTarihi: string;
    adres: string;
    sehir: string;
    kdv: number;
    projeDurumu: string;
}

// Derive a mock completion % from project status + dates
const getProgress = (proje: Proje): number => {
    if (proje.projeDurumu === 'Tamamlandı') return 100;
    if (proje.projeDurumu === 'Gelecek') return 0;
    if (!proje.baslamaTarihi || !proje.bitisTarihi) return 40;
    const start = new Date(proje.baslamaTarihi).getTime();
    const end   = new Date(proje.bitisTarihi).getTime();
    const now   = Date.now();
    if (now >= end) return 95;
    if (now <= start) return 5;
    return Math.round(((now - start) / (end - start)) * 90) + 5;
};

const durumuMeta: Record<string, { label: string; bg: string; color: string; dot: string; pulse?: boolean }> = {
    'Devam Ediyor': { label: 'Devam Ediyor', bg: '#fef3c7', color: '#b45309', dot: '#f59e0b', pulse: true },
    'Tamamlandı':   { label: 'Tamamlandı',   bg: '#dcfce7', color: '#15803d', dot: '#22c55e' },
    'Gelecek':      { label: 'Gelecek',       bg: '#e0f2fe', color: '#0369a1', dot: '#38bdf8' },
};
const getDurum = (d: string) => durumuMeta[d] ?? { label: d, bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };

// Initials avatar for projects without logo
const projeColors = [
    { bg: '#dbeafe', color: '#1d4ed8' }, { bg: '#ede9fe', color: '#6d28d9' },
    { bg: '#dcfce7', color: '#15803d' }, { bg: '#fef3c7', color: '#b45309' },
    { bg: '#fee2e2', color: '#b91c1c' }, { bg: '#ffedd5', color: '#c2410c' },
];
const getProjeColor = (name: string) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return projeColors[Math.abs(h) % projeColors.length];
};

const Projeler: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [daireler, setDaireler] = useState<DaireOzet[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<ProjeForm>({
        projeAdi: '', ada: '', parsel: '',
        baslamaTarihi: '', bitisTarihi: '',
        adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek'
    });

    // ── Proje Dashboard: görünüm modu, arama/filtre ─────────────────────────
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [aramaMetni, setAramaMetni] = useState('');
    const [durumFiltre, setDurumFiltre] = useState('Devam Ediyor');
    const [sehirFiltre, setSehirFiltre] = useState('');

    // ── "Dış Cephe / 3D" hızlı aksiyonu: seçili proje için Görünümler modalı ─
    const [gorunumProje, setGorunumProje] = useState<Proje | null>(null);
    const [gorunumYukleniyor, setGorunumYukleniyor] = useState(false);
    const [gorunumDaireler, setGorunumDaireler] = useState<any[]>([]);
    const [gorunumBloklar, setGorunumBloklar] = useState<any[]>([]);
    const [gorunumKatlar, setGorunumKatlar] = useState<any[]>([]);
    const [gorunumCepheler, setGorunumCepheler] = useState<any[]>([]);
    const [gorunumManzaralar, setGorunumManzaralar] = useState<any[]>([]);
    const [gorunumDaireTipleri, setGorunumDaireTipleri] = useState<any[]>([]);

    // Proje kartı kapak görseli: yeni bir "kapak fotoğrafı" alanı eklemek yerine, projenin
    // Görünümler'inde zaten yüklü olan gerçek cephe/render fotoğrafı kullanılıyor (mevcut veri).
    const [projeKapaklari, setProjeKapaklari] = useState<Record<number, string>>({});
    // Kapak dosyası silinmiş/bozuksa (404) çıplak alt metin yerine nötr ikona düşer.
    const [kapakHatali, setKapakHatali] = useState<Set<number>>(new Set());

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {
            toast.error(t('msg.projelerYuklemeHata'));
        }
    };

    // Dashboard KPI'ları ve proje kartlarındaki metrikler için — mevcut endpoint'ler
    // yeniden kullanılıyor (Daireler Listesi ve Sözleşmeler sayfalarındaki ile aynı),
    // yeni bir backend endpoint'i eklenmedi.
    const fetchDaireOzet = async () => {
        try {
            const response = await axiosInstance.get('/api/Daireler?aktifMi=true');
            setDaireler(response.data || []);
        } catch { /* sessiz */ }
    };

    useEffect(() => { fetchProjeler(); fetchDaireOzet(); }, []);

    // Her proje için Görünümler'deki ilk gerçek cephe/render fotoğrafını kapak olarak çeker.
    useEffect(() => {
        if (projeler.length === 0) return;
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(projeler.map(async (p): Promise<[number, string | null]> => {
                try {
                    const res = await axiosInstance.get(`/api/ProjeGorunumleri?projeID=${p.projeID}`);
                    const gorunumler: any[] = res.data || [];
                    for (const g of gorunumler) {
                        if (g.arkaplanGorsel) return [p.projeID, g.arkaplanGorsel];
                        const cepheGorsel = (g.cepheler || []).find((c: any) => c.gorselYolu)?.gorselYolu;
                        if (cepheGorsel) return [p.projeID, cepheGorsel];
                    }
                    return [p.projeID, null];
                } catch { return [p.projeID, null]; }
            }));
            if (cancelled) return;
            const map: Record<number, string> = {};
            entries.forEach(([id, url]) => { if (url) map[id] = url; });
            setProjeKapaklari(map);
        })();
        return () => { cancelled = true; };
    }, [projeler]);

    // Proje başına: toplam / satılan (Sözleşme) / rezerve / boş (Satışta) daire sayısı ve
    // henüz satılmamış dairelerin toplam liste fiyatı.
    const projeMetrikleri = useMemo(() => {
        const map = new Map<number, { toplam: number; satilan: number; rezerve: number; bos: number; kalanTutar: number; daireTipleri: string[] }>();
        const tipSetleri = new Map<number, Set<string>>();
        daireler.forEach(d => {
            if (!map.has(d.projeID)) {
                map.set(d.projeID, { toplam: 0, satilan: 0, rezerve: 0, bos: 0, kalanTutar: 0, daireTipleri: [] });
                tipSetleri.set(d.projeID, new Set());
            }
            const m = map.get(d.projeID)!;
            m.toplam += 1;
            if (d.daireDurumu === 'Sözleşme') m.satilan += 1;
            else {
                if (d.daireDurumu === 'Rezerve') m.rezerve += 1;
                else m.bos += 1;
                m.kalanTutar += d.listeFiyati || 0;
            }
            if (d.daireTipi?.tip) tipSetleri.get(d.projeID)!.add(d.daireTipi.tip);
        });
        tipSetleri.forEach((set, projeID) => {
            map.get(projeID)!.daireTipleri = Array.from(set).sort();
        });
        return map;
    }, [daireler]);

    const sehirListesi = useMemo(
        () => Array.from(new Set(projeler.map(p => p.sehir).filter((s): s is string => !!s))).sort(),
        [projeler]
    );

    const filtrelenmisProjeler = useMemo(() => {
        const q = aramaMetni.trim().toLocaleLowerCase('tr-TR');
        return projeler.filter(p => {
            if (durumFiltre && p.projeDurumu !== durumFiltre) return false;
            if (sehirFiltre && p.sehir !== sehirFiltre) return false;
            if (q) {
                const hay = `${p.projeAdi} ${p.sehir || ''} ${p.adres || ''}`.toLocaleLowerCase('tr-TR');
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [projeler, aramaMetni, durumFiltre, sehirFiltre]);

    const acDaireMatrisi = (proje: Proje) => {
        navigate('/daireler/liste', { state: { projeID: proje.projeID } });
    };

    const acDisCepheGorunumu = async (proje: Proje) => {
        setGorunumYukleniyor(true);
        try {
            const [dRes, bRes, kRes, cRes, mRes, dtRes] = await Promise.all([
                axiosInstance.get(`/api/Daireler?projeID=${proje.projeID}&hepsi=true`),
                axiosInstance.get(`/api/Bloklar/proje/${proje.projeID}`),
                axiosInstance.get('/api/Katlar'),
                axiosInstance.get('/api/Cepheler'),
                axiosInstance.get('/api/Manzaralar'),
                axiosInstance.get('/api/DaireTipleri'),
            ]);
            setGorunumDaireler(dRes.data?.items || dRes.data || []);
            setGorunumBloklar((bRes.data || []).map((b: any) => ({ id: b.blokID, ad: b.blokAdi })));
            setGorunumKatlar((kRes.data || []).map((k: any) => ({ id: k.katID, ad: k.katAdi })));
            setGorunumCepheler((cRes.data || []).map((c: any) => ({ id: c.cepheID, ad: c.cepheAdi })));
            setGorunumManzaralar((mRes.data || []).map((m: any) => ({ id: m.manzaraID, ad: m.manzaraAdi })));
            setGorunumDaireTipleri((dtRes.data || []).map((dt: any) => ({ id: dt.daireTipID, ad: dt.tip })));
            setGorunumProje(proje);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setGorunumYukleniyor(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            let logoFileName = null;
            if (selectedFile) {
                const fd = new FormData();
                fd.append('file', selectedFile);
                fd.append('aciklama', 'Proje Logo');
                const up = await axiosInstance.post('/api/Dosyalar/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                logoFileName = up.data.dosyaAdi;
            }
            const response = await axiosInstance.post('/api/Projeler', {
                ...formData, projeLogo: logoFileName, aktifMi: true
            });
            toast.success(t('msg.projeEklendi'));
            setShowModal(false);
            fetchProjeler();
            navigate(`/projeler/${response.data.projeID}`);
        } catch {
            toast.error(t('msg.projeEklenirkenHata'));
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type === 'image/jpeg' || file.type === 'image/png') {
            setSelectedFile(file);
        } else {
            toast.error(t('msg.sadecePngJpg'));
            e.target.value = '';
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        setFormData({ projeAdi: '', ada: '', parsel: '', baslamaTarihi: '', bitisTarihi: '', adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek' });
    };

    const aktifProjeler   = projeler.filter(p => p.projeDurumu === 'Devam Ediyor').length;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .pj-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 24px;
                    background: #ffffff;
                }

                /* ── Header ── */
                .pj-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 40px; flex-wrap: wrap; gap: 14px; }
                .pj-header-left { display: flex; flex-direction: column; gap: 3px; }
                .pj-title  { font-size: 20px; font-weight: 600; color: #111827; letter-spacing: -0.4px; margin: 0; }
                .pj-subtitle { font-size: 13px; color: #94a3b8; }
                .pj-breadcrumb { font-size: 13px; color: #6b7280; margin-bottom: 4px; display: flex; align-items: center; }
                .pj-breadcrumb .sep { margin: 0 6px; }
                .pj-breadcrumb .current { color: #1a1a1a; font-weight: 500; }
                .pj-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #cd6906; color: #fff;
                    border: none; border-radius: 8px;
                    font-size: 13.5px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.32);
                    transition: background 0.15s, transform 0.12s, box-shadow 0.15s;
                }
                .pj-add-btn:hover { background: #b85d05; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
                .pj-add-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Stat cards ── */
                .pj-stats { display: flex; gap: 16px; margin-bottom: 26px; flex-wrap: wrap; }
                .pj-stat {
                    background: #fff; border-radius: 12px; padding: 16px 20px;
                    display: flex; align-items: center; gap: 14px; flex: 1; min-width: 130px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                }
                .pj-stat-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                .pj-stat-icon svg { width: 18px; height: 18px; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
                .pj-stat-val  { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
                .pj-stat-lbl  { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 3px; }

                /* ── Filter bar ── */
                .pj-filterbar { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
                .pj-search {
                    flex: 1; min-width: 220px; display: flex; align-items: center; gap: 8px;
                    background: #fff; border: 1.5px solid #e2e8f0; border-radius: 9px;
                    padding: 9px 14px; box-shadow: 0 1px 2px rgba(15,23,42,0.03);
                }
                .pj-search input { border: none; outline: none; font-size: 13.5px; font-family: inherit; flex: 1; color: #0f172a; background: transparent; }
                .pj-filter-select {
                    padding: 9px 12px; border: 1.5px solid #e2e8f0; border-radius: 9px;
                    background: #fff; font-size: 13px; font-family: inherit; color: #475569;
                    outline: none; cursor: pointer;
                }
                .pj-view-toggle { display: flex; border: 1.5px solid #e2e8f0; border-radius: 9px; overflow: hidden; flex-shrink: 0; }
                .pj-view-toggle button {
                    width: 38px; height: 38px; display: flex; align-items: center; justify-content: center;
                    border: none; background: #fff; color: #94a3b8; cursor: pointer; transition: background 0.15s, color 0.15s;
                }
                .pj-view-toggle button + button { border-left: 1.5px solid #e2e8f0; }
                .pj-view-toggle button.active { background: #cd6906; color: #fff; }

                /* ── Project grid (cards) ── */
                .pj-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 18px; }
                .pj-card {
                    background: #fff; border-radius: 14px; overflow: hidden;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                    border: 1.5px solid transparent; transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
                    display: flex; flex-direction: column;
                }
                .pj-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,23,42,0.1); border-color: #e0eaff; }
                .pj-card-banner { position: relative; padding: 22px 18px; cursor: pointer; display: flex; align-items: flex-start; justify-content: space-between; min-height: 74px; }
                .pj-card-logo { width: 52px; height: 52px; border-radius: 12px; object-fit: cover; box-shadow: 0 2px 8px rgba(15,23,42,0.15); }
                .pj-card-logo-avatar { display: flex; align-items: center; justify-content: center; font-size: 17px; font-weight: 800; background: #fff; }
                .pj-card-badge { position: absolute; top: 14px; right: 14px; }
                .pj-card-body { padding: 4px 18px 16px; cursor: pointer; flex: 1; }
                .pj-seg-track { display: flex; height: 7px; border-radius: 4px; overflow: hidden; background: #f1f5f9; margin: 12px 0 10px; }
                .pj-seg-track > div { height: 100%; transition: width 0.4s ease; }
                .pj-card-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 4px; }
                .pj-cm-val { font-size: 16px; font-weight: 800; color: #0f172a; text-align: center; }
                .pj-cm-lbl { font-size: 10.5px; color: #94a3b8; text-align: center; font-weight: 500; margin-top: 1px; }
                .pj-card-actions { display: flex; border-top: 1px solid #f1f5f9; }
                .pj-card-actions button {
                    flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px;
                    padding: 10px 4px; border: none; background: #fff; color: #64748b;
                    font-size: 10.5px; font-weight: 600; font-family: inherit; cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                }
                .pj-card-actions button:hover:not(:disabled) { background: #fff8f0; color: #cd6906; }
                .pj-card-actions button:disabled { opacity: 0.5; cursor: not-allowed; }
                .pj-card-actions button + button { border-left: 1px solid #f1f5f9; }

                /* ── Project list ── */
                .pj-list { display: flex; flex-direction: column; gap: 12px; }

                /* ── Project row card ── */
                .pj-row {
                    background: #fff; border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.05);
                    padding: 18px 22px;
                    display: flex; align-items: center; gap: 20px;
                    cursor: pointer;
                    transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
                    border: 1.5px solid transparent;
                }
                .pj-row:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(15,23,42,0.08);
                    border-color: #e0eaff;
                }

                /* Logo/avatar */
                .pj-logo-wrap { flex-shrink: 0; }
                .pj-logo-img {
                    width: 56px; height: 56px; border-radius: 14px;
                    object-fit: cover;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.12);
                }
                .pj-logo-avatar {
                    width: 56px; height: 56px; border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 18px; font-weight: 800; letter-spacing: -0.5px;
                }

                /* Identity */
                .pj-identity { flex: 1; min-width: 0; }
                .pj-proje-adi {
                    font-size: 16px; font-weight: 700; color: #0f172a;
                    letter-spacing: -0.2px;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .pj-proje-sub { font-size: 12.5px; color: #94a3b8; margin-top: 3px; font-weight: 400; }
                .pj-takip    { font-size: 11px; color: #cbd5e1; margin-top: 2px; font-family: 'SF Mono','Consolas',monospace; }

                /* Progress */
                .pj-progress-col { width: 140px; flex-shrink: 0; }
                .pj-progress-lbl { font-size: 11px; color: #94a3b8; font-weight: 500; margin-bottom: 5px; display: flex; justify-content: space-between; }
                .pj-progress-lbl span { color: #475569; font-weight: 600; }
                .pj-track { height: 5px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
                .pj-fill  { height: 100%; border-radius: 9999px; transition: width 0.6s ease; }

                /* Status pill */
                .pj-pill {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 5px 12px; border-radius: 20px;
                    font-size: 12px; font-weight: 600; white-space: nowrap;
                    flex-shrink: 0;
                }
                .pj-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                @keyframes pjPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
                .pj-pill-dot-pulse { animation: pjPulse 1.5s ease-in-out infinite; }

                /* Detail button */
                .pj-detail-btn {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 8px 16px; border-radius: 9px;
                    border: 1.5px solid #e2e8f0; background: transparent;
                    font-size: 13px; font-weight: 600; color: #475569;
                    font-family: inherit; cursor: pointer; white-space: nowrap;
                    flex-shrink: 0;
                    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.12s;
                }
                .pj-row:hover .pj-detail-btn {
                    background: #cd6906; border-color: #cd6906; color: #fff;
                    transform: translateX(2px);
                }
                .pj-detail-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2.5; fill: none; transition: transform 0.15s; }
                .pj-row:hover .pj-detail-btn svg { transform: translateX(2px); }

                /* Empty */
                .pj-empty { text-align: center; padding: 64px 20px; color: #94a3b8; background: #fff; border-radius: 16px; box-shadow: 0 1px 4px rgba(15,23,42,0.06); }
                .pj-empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.5; }
                .pj-empty-text { font-size: 16px; font-weight: 500; }
                .pj-empty-sub  { font-size: 13px; color: #b0bec5; margin-top: 6px; }

                /* ── Modal ── */
                .pj-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                }
                .pj-modal {
                    background: #fff; border-radius: 18px;
                    width: 100%; max-width: 680px; max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: pjModalIn 0.18s ease;
                }
                @keyframes pjModalIn { from{opacity:0;transform:translateY(-14px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
                .pj-modal-header {
                    padding: 22px 26px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                    position: sticky; top: 0; background: #fff; z-index: 1;
                    border-radius: 18px 18px 0 0;
                }
                .pj-modal-title  { font-size: 17px; font-weight: 700; color: #0f172a; margin: 0; }
                .pj-modal-close  {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .pj-modal-close:hover { background: #e2e8f0; }
                .pj-modal-body  { padding: 22px 26px; }
                .pj-modal-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .pj-modal-full  { grid-column: 1 / -1; }
                .pj-mfield      { display: flex; flex-direction: column; gap: 6px; }
                .pj-mfield label {
                    font-size: 11.5px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em; color: #64748b;
                }
                .pj-mfield input, .pj-mfield select, .pj-mfield textarea {
                    padding: 10px 13px; border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; background: #fff; box-sizing: border-box; width: 100%;
                    transition: border-color 0.15s, box-shadow 0.15s;
                }
                .pj-mfield input:focus, .pj-mfield select:focus, .pj-mfield textarea:focus {
                    border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
                }
                .pj-mfield textarea { resize: vertical; min-height: 76px; line-height: 1.5; }
                .pj-mfield-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; }
                .pj-modal-footer {
                    padding: 16px 26px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                    position: sticky; bottom: 0; background: #fff;
                    border-radius: 0 0 18px 18px;
                }
                .pj-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    transition: background 0.15s;
                }
                .pj-btn-cancel:hover { background: #f1f5f9; }
                .pj-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .pj-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .pj-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .pj-spinner {
                    width: 13px; height: 13px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: pjSpin .7s linear infinite;
                }
                @keyframes pjSpin { to { transform: rotate(360deg); } }

                /* ── Responsive ── */
                @media (max-width: 991px) {
                    .pj-page { padding: 18px 14px; }
                    .pj-row { flex-wrap: wrap; padding: 16px 18px; gap: 12px; }
                    .pj-identity { flex: 1 1 0; min-width: 120px; }
                    .pj-progress-col { width: auto; flex: 1 1 100%; order: 5; }
                    .pj-pill { order: 6; }
                    .pj-detail-btn { order: 7; margin-left: auto; }
                    .pj-stats { gap: 10px; }
                    .pj-stat { min-width: calc(50% - 10px); flex: 0 0 calc(50% - 10px); }
                }

                @media (max-width: 576px) {
                    .pj-page { padding: 14px 8px; }
                    .pj-row { gap: 10px; padding: 14px 14px; }
                    .pj-logo-img, .pj-logo-avatar { width: 44px; height: 44px; border-radius: 11px; font-size: 15px; }
                    .pj-proje-adi { font-size: 14px; }
                    .pj-stat { min-width: 100%; flex: 0 0 100%; }
                    .pj-stat-val { font-size: 20px; }
                    .pj-modal-grid { grid-template-columns: 1fr; }
                    .pj-title { font-size: 18px; }
                    .pj-detail-btn { padding: 7px 12px; font-size: 12px; }
                    .pj-grid { grid-template-columns: 1fr; }
                    .pj-search { min-width: 100%; order: -1; }
                }
            `}</style>

            <div className="min-h-screen bg-slate-50 p-6" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                {/* Arama / filtre çubuğu — referans fotodaki kapsül (pill) buton/alan stiliyle */}
                        <div className="bg-white rounded-full shadow-sm px-5 py-2.5 mb-6 flex flex-wrap items-center gap-3">
                            <div className="relative flex-1 min-w-[240px]">
                                <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Proje adı veya lokasyon ara..."
                                    value={aramaMetni}
                                    onChange={e => setAramaMetni(e.target.value)}
                                    className="w-full bg-slate-50 border-0 rounded-full pl-10 pr-3.5 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:ring-2 focus:ring-[#cd6906]/15 focus:bg-white"
                                    style={{ border: 'none' }}
                                />
                            </div>
                            <select
                                className="bg-slate-50 border-0 text-slate-700 text-sm rounded-full px-4 py-2 outline-none cursor-pointer transition-colors focus:ring-2 focus:ring-[#cd6906]/15 focus:bg-white min-w-[150px]"
                                style={{ border: 'none' }}
                                value={durumFiltre} onChange={e => setDurumFiltre(e.target.value)}
                            >
                                <option value="">Tüm Durumlar</option>
                                <option value="Devam Ediyor">Devam Ediyor</option>
                                <option value="Tamamlandı">Tamamlandı</option>
                                <option value="Gelecek">Gelecek</option>
                            </select>
                            {sehirListesi.length > 0 && (
                                <select
                                    className="bg-slate-50 border-0 text-slate-700 text-sm rounded-full px-4 py-2 outline-none cursor-pointer transition-colors focus:ring-2 focus:ring-[#cd6906]/15 focus:bg-white min-w-[150px]"
                                    style={{ border: 'none' }}
                                    value={sehirFiltre} onChange={e => setSehirFiltre(e.target.value)}
                                >
                                    <option value="">Tüm İller</option>
                                    {sehirListesi.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            )}
                            <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-full shrink-0">
                                <button
                                    className={`border-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors ${viewMode === 'grid' ? 'bg-slate-900 text-white shadow-sm' : 'bg-transparent text-slate-400 hover:text-slate-600'}`}
                                    onClick={() => setViewMode('grid')} title="Kart Görünümü"
                                ><LayoutGrid size={14} /></button>
                                <button
                                    className={`border-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white shadow-sm' : 'bg-transparent text-slate-400 hover:text-slate-600'}`}
                                    onClick={() => setViewMode('list')} title="Liste Görünümü"
                                ><List size={14} /></button>
                            </div>
                        </div>

                <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5' : 'pj-list'}>
                    {filtrelenmisProjeler.length === 0 ? (
                        <div className={viewMode === 'grid' ? 'col-span-full' : ''}>
                            <div className="pj-empty">
                                <div className="pj-empty-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cbd5e1" width="48" height="48">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                                    </svg>
                                </div>
                                <div className="pj-empty-text">Henüz proje eklenmedi</div>
                                <div className="pj-empty-sub">Yeni proje eklemek için sağ üstteki butona tıklayın</div>
                            </div>
                        </div>
                    ) : viewMode === 'list' ? (
                        filtrelenmisProjeler.map(proje => {
                            const durum    = getDurum(proje.projeDurumu);
                            const progress = getProgress(proje);
                            const avColor  = getProjeColor(proje.projeAdi);
                            const initials = proje.projeAdi.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
                            const subText  = [proje.sehir, proje.adres].filter(Boolean).join(', ') || 'Konum belirtilmemiş';
                            const progressColor = proje.projeDurumu === 'Tamamlandı' ? '#22c55e' : proje.projeDurumu === 'Gelecek' ? '#38bdf8' : '#f59e0b';
                            return (
                                <div key={proje.projeID} className="pj-row"
                                    onClick={() => navigate(`/projeler/${proje.projeID}/sayfa`)}
                                    onMouseEnter={() => setHoveredRow(proje.projeID)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                >
                                    <div className="pj-logo-wrap">
                                        {proje.projeLogo ? (
                                            <img src={`${API_BASE_URL}/userFiles/${proje.projeLogo}`} alt={proje.projeAdi} className="pj-logo-img" />
                                        ) : (
                                            <div className="pj-logo-avatar" style={{ background: avColor.bg, color: avColor.color }}>{initials}</div>
                                        )}
                                    </div>
                                    <div className="pj-identity">
                                        <div className="pj-proje-adi">{proje.projeAdi}</div>
                                        <div className="pj-proje-sub">{subText}</div>
                                        {proje.takipNo && <div className="pj-takip">{proje.takipNo}</div>}
                                    </div>
                                    <div className="pj-progress-col">
                                        <div className="pj-progress-lbl">İlerleme <span>{progress}%</span></div>
                                        <div className="pj-track">
                                            <div className="pj-fill" style={{ width: `${progress}%`, background: progressColor }} />
                                        </div>
                                    </div>
                                    <span className="pj-pill" style={{ background: durum.bg, color: durum.color }}>
                                        <span className={`pj-pill-dot${durum.pulse ? ' pj-pill-dot-pulse' : ''}`} style={{ background: 'currentColor' }} />
                                        {durum.label}
                                    </span>
                                    <button className="pj-detail-btn" onClick={e => { e.stopPropagation(); navigate(`/projeler/${proje.projeID}`); }}>
                                        İncele
                                        <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        filtrelenmisProjeler.map(proje => {
                            const subText = [proje.sehir, proje.adres].filter(Boolean).join(', ') || 'Konum belirtilmemiş';
                            const m = projeMetrikleri.get(proje.projeID) ?? { toplam: 0, satilan: 0, rezerve: 0, bos: 0, kalanTutar: 0, daireTipleri: [] as string[] };
                            const kapak = projeKapaklari[proje.projeID];
                            return (
                                <div key={proje.projeID} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300 flex flex-col">
                                    {/* A) Görsel katmanı — büyük kapak fotoğrafı */}
                                    <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-700 to-slate-900 cursor-pointer" onClick={() => navigate(`/projeler/${proje.projeID}/sayfa`)}>
                                        {kapak && !kapakHatali.has(proje.projeID) ? (
                                            <img
                                                src={`${API_BASE_URL}/api/ProjeGorunumleri/gorsel/${kapak}`} alt={proje.projeAdi}
                                                className="w-full h-full object-cover"
                                                onError={() => setKapakHatali(prev => new Set(prev).add(proje.projeID))}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                <Building2 size={40} strokeWidth={1.5} />
                                            </div>
                                        )}
                                        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" />
                                        <span className="absolute top-3 left-3 inline-flex items-center gap-2 bg-slate-900/70 backdrop-blur-md text-white text-xs font-medium px-3 py-1.5 rounded-full border border-white/15">
                                            <span className="w-2 h-2 rounded-full inline-block bg-white/80" />
                                            {proje.projeDurumu}
                                        </span>
                                        <div className="absolute bottom-3 left-4 right-4 text-white">
                                            <div className="text-lg font-bold drop-shadow-sm">{proje.projeAdi}</div>
                                            <div className="flex items-center gap-1 text-xs text-white/85 mt-0.5">
                                                <MapPin size={12} className="shrink-0" />
                                                {subText}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 flex-1 cursor-pointer" onClick={() => navigate(`/projeler/${proje.projeID}/sayfa`)}>
                                        {/* B) Toplam bölüm sayısı + daire tipleri */}
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-2">
                                            <Grid3x3 size={13} className="text-slate-400" />
                                            {m.toplam} bölüm
                                        </div>
                                        {m.daireTipleri.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {m.daireTipleri.map(tip => (
                                                    <span key={tip} className="bg-slate-50 border border-slate-100 text-slate-600 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                                                        {tip}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {/* D) Alt aksiyon butonları */}
                                    <div className="border-t border-slate-100 p-4 pt-3 flex gap-2">
                                        <button
                                            onClick={() => acDaireMatrisi(proje)} title="Daire Matrisi"
                                            className="border-0 flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 text-xs font-semibold py-2.5 px-4 rounded-lg shadow-xs transition-colors whitespace-nowrap"
                                        >
                                            <Grid3x3 size={13} /> Daire Matrisi
                                        </button>
                                        <button
                                            onClick={() => acDisCepheGorunumu(proje)} title="3D / Cephe" disabled={gorunumYukleniyor}
                                            className="border-0 inline-flex items-center justify-center gap-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-medium py-2.5 px-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            <Camera size={13} /> 3D / Cephe
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Dış Cephe / 3D — Görünümler modalı (salt okunur) */}
            {gorunumProje && (
                <ProjeGorunumleri
                    projeID={gorunumProje.projeID}
                    daireler={gorunumDaireler}
                    bloklar={gorunumBloklar}
                    katlar={gorunumKatlar}
                    cepheler={gorunumCepheler}
                    manzaralar={gorunumManzaralar}
                    daireTipleri={gorunumDaireTipleri}
                    onClose={() => setGorunumProje(null)}
                    readOnly={true}
                />
            )}

            {/* Modal */}
            {showModal && (
                <div className="pj-overlay" onClick={handleCloseModal}>
                    <div className="pj-modal" onClick={e => e.stopPropagation()}>
                        <div className="pj-modal-header">
                            <h2 className="pj-modal-title">{t('proje.ekle')}</h2>
                            <button className="pj-modal-close" onClick={handleCloseModal} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="pj-modal-body">
                                <div className="pj-modal-grid">
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('proje.projeAdi')} <span style={{ color: '#e11d48' }}>*</span></label>
                                        <input
                                            type="text"
                                            placeholder={t('label.ara')}
                                            value={formData.projeAdi}
                                            onChange={e => setFormData({ ...formData, projeAdi: e.target.value })}
                                            maxLength={50}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.sehir')}</label>
                                        <input
                                            type="text"
                                            placeholder={t('label.ara')}
                                            value={formData.sehir}
                                            onChange={e => setFormData({ ...formData, sehir: e.target.value })}
                                            maxLength={50}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.projeDurumu')}</label>
                                        <select value={formData.projeDurumu} onChange={e => setFormData({ ...formData, projeDurumu: e.target.value })}>
                                            <option value="Gelecek">Gelecek</option>
                                            <option value="Devam Ediyor">Devam Ediyor</option>
                                            <option value="Tamamlandı">{t('durum.tamamlandi')}</option>
                                        </select>
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.baslamaTarihi')}</label>
                                        <TrDateInput
                                            value={formData.baslamaTarihi}
                                            onChange={e => setFormData({ ...formData, baslamaTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.bitisTarihi')}</label>
                                        <TrDateInput
                                            value={formData.bitisTarihi}
                                            onChange={e => setFormData({ ...formData, bitisTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('label.ada')}</label>
                                        <input type="text" placeholder="Ada no" value={formData.ada} onChange={e => setFormData({ ...formData, ada: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('label.parsel')}</label>
                                        <input type="text" placeholder="Parsel no" value={formData.parsel} onChange={e => setFormData({ ...formData, parsel: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pj-mfield">
                                        <label>{t('proje.kdv')}</label>
                                        <input
                                            type="number" min="0" max="100"
                                            value={formData.kdv}
                                            onChange={e => setFormData({ ...formData, kdv: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('label.adres')}</label>
                                        <textarea
                                            placeholder="Proje adresi..."
                                            value={formData.adres}
                                            onChange={e => setFormData({ ...formData, adres: e.target.value })}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="pj-mfield pj-modal-full">
                                        <label>{t('proje.logo')}</label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="pj-mfield-hint">Sadece JPG veya PNG · Maks. 10 MB</div>
                                    </div>
                                </div>
                            </div>
                            <div className="pj-modal-footer">
                                <button type="button" className="pj-btn-cancel" onClick={handleCloseModal}>{t('btn.iptal')}</button>
                                <button type="submit" className="pj-btn-save" disabled={saving}>
                                    {saving && <span className="pj-spinner" />}
                                    {saving ? 'Kaydediliyor...' : t('btn.kaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Projeler;